import { API_BASE_URL, ApiError, clearToken, getToken, request, requestSiteMutation } from "./client";
import type { BuildExecution, Site } from "./types";
import type { Prospect, ProspectHarvestDetail, ProspectHarvestResult, ProspectSearch, ProspectSearchResult } from '@prototypical/types';

async function prospectPhoto(placeId: string, photoId: string, signal?: AbortSignal): Promise<Blob> {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}/api/admin/prospects/harvests/${encodeURIComponent(placeId)}/photos/${encodeURIComponent(photoId)}`, {
    credentials: 'include', signal, headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (response.status === 401) clearToken();
    throw new ApiError(response.status, await response.json().catch(() => ({ error: 'Photo download failed. Please retry.' })));
  }
  return response.blob();
}

export type AdminBuild = {
  id: string; name: string; source: "engine" | "bundle"; ownerId: string | null; briefId: string | null;
  status: string; createdAt: number | null; updatedAt: number | null; usdUsed: number | null;
  previewUrl: string | null; siteId: string | null; subdomain: string | null;
  execution?: BuildExecution;
  engineVersion?: string;
  evaluationGroup?: string;
  metadata?: Record<string, unknown>;
  membershipStatus?: "linked" | "unassigned" | "conflict";
  canDelete?: false;
};

export const adminApi = {
  deleteAdminBuild: (id: string) => request<{ ok: true }>(`/api/admin/builds/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  prospectConfig: () => request<{ mapsBrowserKey: string | null }>('/api/admin/prospects/config'),
  prospectSearch: (search: ProspectSearch) => request<ProspectSearchResult>('/api/admin/prospects/search', { method: 'POST', body: JSON.stringify(search) }),
  prospectHarvests: (cursor?: string, signal?: AbortSignal) => request<{ hits: Prospect[]; nextCursor: string | null }>(`/api/admin/prospects/harvests?${new URLSearchParams(cursor ? { cursor } : {})}`, { signal }),
  prospectHarvestDetail: (placeId: string, signal?: AbortSignal) => request<ProspectHarvestDetail>(`/api/admin/prospects/harvests/${encodeURIComponent(placeId)}`, { signal }),
  resolveProspectLocation: (placeId: string, signal?: AbortSignal) => request<Prospect>(`/api/admin/prospects/harvests/${encodeURIComponent(placeId)}/location`, { method: 'POST', signal }),
  prospectPhoto,
  harvestProspect: (placeId: string) => request<ProspectHarvestResult>('/api/admin/prospects/harvest', { method: 'POST', body: JSON.stringify({ placeId }) }),
  adminBuilds: (source: "engine" | "bundles", cursor?: string, signal?: AbortSignal) =>
    request<{ builds: AdminBuild[]; nextCursor: string | null }>(
      `/api/admin/builds?${new URLSearchParams({ source, ...(cursor ? { cursor } : {}) })}`, { signal },
    ),
  adminStats: () =>
    request<{
      totalUsers: number;
      usersByRole: Array<{ role: string; count: number }>;
      totalSites: number;
      sitesByStatus: Array<{ status: string; count: number }>;
      /** Accounts that own at least one site — the honest "customers" number. */
      siteOwners?: number;
    }>("/api/admin/stats"),
  searchProspects: (q: string) =>
    request<{ hits: Array<{ placeId: string; name: string; address: string }> }>(
      `/api/admin/prospects?q=${encodeURIComponent(q)}`,
    ),
  createSite: (params: { ownerUserId: string; subdomain: string; placeId?: string }) =>
    requestSiteMutation<Site>("/api/admin/sites", {
      method: "POST",
      body: JSON.stringify(params),
    }),
};
